/**
 * Fleet Overview Component
 *
 * Shows all vessels with RAG compliance status.
 * Main view of the Fleet Dashboard.
 */

import React from 'react';
import {
  Ship,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  ChevronRight,
  FileCheck,
  Wrench,
  Users,
  Shield
} from 'lucide-react';

import { useFleetData } from '../../../../hooks/domain/fleet/useFleetData';
import { useVessel } from '../../../../contexts/VesselContext';

/**
 * Status indicator component
 */
const StatusIndicator = ({ status, size = 'md' }) => {
  const sizeClasses = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
    lg: 'h-6 w-6'
  };

  const config = {
    green: { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-100' },
    amber: { icon: AlertCircle, color: 'text-amber-500', bg: 'bg-amber-100' },
    red: { icon: AlertTriangle, color: 'text-red-500', bg: 'bg-red-100' }
  };

  const { icon: Icon, color, bg } = config[status] || config.green;

  return (
    <div className={`${bg} rounded-full p-1`}>
      <Icon className={`${sizeClasses[size]} ${color}`} />
    </div>
  );
};

/**
 * Mini status badge for summary items
 */
const MiniStatus = ({ label, value, status }) => {
  const statusColors = {
    green: 'text-green-600',
    amber: 'text-amber-600',
    red: 'text-red-600'
  };

  return (
    <div className="text-sm">
      <span className="text-slate-500">{label}: </span>
      <span className={statusColors[status] || 'text-slate-700'}>
        {value}
      </span>
    </div>
  );
};

/**
 * Vessel Card Component
 */
const VesselCard = ({ vesselData, onViewVessel }) => {
  const { vessel, ragStatus, summary } = vesselData;

  const statusLabels = {
    green: 'Compliant',
    amber: 'Attention Needed',
    red: 'Action Required'
  };

  const statusBgColors = {
    green: 'border-green-200 bg-green-50',
    amber: 'border-amber-200 bg-amber-50',
    red: 'border-red-200 bg-red-50'
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden hover:shadow-md transition-shadow">
      {/* Header */}
      <div className={`px-4 py-3 border-b ${statusBgColors[ragStatus]}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Ship className="h-5 w-5 text-slate-600" />
            <div>
              <h3 className="font-semibold text-slate-800">{vessel.name}</h3>
              <p className="text-xs text-slate-500">{vessel.type || 'Workboat'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <StatusIndicator status={ragStatus} size="lg" />
            <span className={`text-sm font-medium ${
              ragStatus === 'green' ? 'text-green-700' :
              ragStatus === 'amber' ? 'text-amber-700' : 'text-red-700'
            }`}>
              {statusLabels[ragStatus]}
            </span>
          </div>
        </div>
      </div>

      {/* Summary Grid */}
      <div className="p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Certificates */}
          <div className="flex items-start gap-2">
            <FileCheck className={`h-4 w-4 mt-0.5 ${
              summary.certificates.status === 'red' ? 'text-red-500' :
              summary.certificates.status === 'amber' ? 'text-amber-500' : 'text-green-500'
            }`} />
            <div>
              <p className="text-xs text-slate-500">Certificates</p>
              <p className="text-sm font-medium">
                {summary.certificates.expired > 0 && (
                  <span className="text-red-600">{summary.certificates.expired} expired</span>
                )}
                {summary.certificates.expired === 0 && summary.certificates.expiring > 0 && (
                  <span className="text-amber-600">{summary.certificates.expiring} expiring</span>
                )}
                {summary.certificates.expired === 0 && summary.certificates.expiring === 0 && (
                  <span className="text-green-600">All valid</span>
                )}
              </p>
            </div>
          </div>

          {/* Drills */}
          <div className="flex items-start gap-2">
            <Shield className={`h-4 w-4 mt-0.5 ${
              summary.drills.status === 'red' ? 'text-red-500' : 'text-green-500'
            }`} />
            <div>
              <p className="text-xs text-slate-500">Drills</p>
              <p className="text-sm font-medium">
                {summary.drills.overdue > 0 ? (
                  <span className="text-red-600">{summary.drills.overdue} overdue</span>
                ) : (
                  <span className="text-green-600">On schedule</span>
                )}
              </p>
            </div>
          </div>

          {/* Defects */}
          <div className="flex items-start gap-2">
            <Wrench className={`h-4 w-4 mt-0.5 ${
              summary.defects.status === 'red' ? 'text-red-500' :
              summary.defects.status === 'amber' ? 'text-amber-500' : 'text-green-500'
            }`} />
            <div>
              <p className="text-xs text-slate-500">Defects</p>
              <p className="text-sm font-medium">
                {summary.defects.critical > 0 && (
                  <span className="text-red-600">{summary.defects.critical} critical</span>
                )}
                {summary.defects.critical === 0 && summary.defects.open > 0 && (
                  <span className="text-amber-600">{summary.defects.open} open</span>
                )}
                {summary.defects.open === 0 && (
                  <span className="text-green-600">None open</span>
                )}
              </p>
            </div>
          </div>

          {/* Crew */}
          <div className="flex items-start gap-2">
            <Users className={`h-4 w-4 mt-0.5 ${
              summary.crew.status === 'red' ? 'text-red-500' :
              summary.crew.status === 'amber' ? 'text-amber-500' : 'text-green-500'
            }`} />
            <div>
              <p className="text-xs text-slate-500">Crew</p>
              <p className="text-sm font-medium">
                {summary.crew.certsExpiring > 0 ? (
                  <span className="text-amber-600">{summary.crew.certsExpiring} certs expiring</span>
                ) : (
                  <span className="text-green-600">All certified</span>
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Action Row */}
        <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
          <p className="text-xs text-slate-400">
            {/* Last activity placeholder */}
            Last updated: {new Date(vesselData.updatedAt).toLocaleString()}
          </p>
          <button
            onClick={() => onViewVessel(vessel.id)}
            className="flex items-center gap-1 text-sm font-medium text-cyan-600 hover:text-cyan-700"
          >
            View Vessel
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Fleet Summary Bar Component
 */
const FleetSummaryBar = ({ summary }) => {
  const items = [
    { label: 'Total Vessels', value: summary.totalVessels, icon: Ship },
    { label: 'Action Required', value: summary.actionRequired, icon: AlertTriangle, color: 'text-red-600' },
    { label: 'Overdue Items', value: summary.totalOverdueItems, icon: AlertCircle, color: summary.totalOverdueItems > 0 ? 'text-red-600' : 'text-slate-600' },
    { label: 'Expiring Certs', value: summary.totalExpiringCerts, icon: FileCheck, color: summary.totalExpiringCerts > 0 ? 'text-amber-600' : 'text-slate-600' },
    { label: 'Critical Defects', value: summary.totalCriticalDefects, icon: Wrench, color: summary.totalCriticalDefects > 0 ? 'text-red-600' : 'text-slate-600' }
  ];

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-6">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {items.map((item, index) => (
          <div key={index} className="flex items-center gap-3">
            <div className="p-2 bg-slate-100 rounded-lg">
              <item.icon className={`h-5 w-5 ${item.color || 'text-slate-600'}`} />
            </div>
            <div>
              <p className="text-xs text-slate-500">{item.label}</p>
              <p className={`text-lg font-semibold ${item.color || 'text-slate-800'}`}>
                {item.value}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Fleet Overview Main Component
 */
const FleetOverview = () => {
  const { vesselsByStatus, fleetSummary, isLoading } = useFleetData();
  const { setCurrentVessel } = useVessel();

  const handleViewVessel = (vesselId) => {
    setCurrentVessel(vesselId);
    // The app will automatically switch to vessel view
    // You might want to add navigation here depending on your routing setup
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-24 bg-slate-200 rounded-xl mb-6" />
          <div className="space-y-4">
            <div className="h-40 bg-slate-200 rounded-xl" />
            <div className="h-40 bg-slate-200 rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  if (vesselsByStatus.length === 0) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 text-center">
          <Ship className="h-12 w-12 text-slate-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-700 mb-2">No Vessels Found</h3>
          <p className="text-slate-500 mb-4">
            Add vessels to your fleet to see them here.
          </p>
          <button className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700">
            <Ship className="h-4 w-4" />
            Add Vessel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Fleet Summary */}
      <FleetSummaryBar summary={fleetSummary} />

      {/* Vessel Cards */}
      <div className="space-y-4">
        {vesselsByStatus.map(vesselData => (
          <VesselCard
            key={vesselData.vesselId}
            vesselData={vesselData}
            onViewVessel={handleViewVessel}
          />
        ))}
      </div>
    </div>
  );
};

export default FleetOverview;
